import React from 'react';
import { BarChart3, TrendingUp, Award, Target } from 'lucide-react';
import { useTranslation } from '../utils/translations';

interface SentenceStatsVisualizationProps {
  stats: {
    totalSentences: number;
    averageAccuracy: number;
    perfectSentences: number;
    goodSentences: number; // 75-99%
    needsPractice: number; // < 75%
    sentenceDetails: Array<{
      index: number;
      percentage: number;
    }>;
  };
  nativeLanguage?: string;
}

export function SentenceStatsVisualization({ stats, nativeLanguage }: SentenceStatsVisualizationProps) {
  const { t, isRTL } = useTranslation(nativeLanguage);

  const getAccuracyColor = (avg: number) => {
    if (avg >= 90) return 'text-green-600';
    if (avg >= 75) return 'text-blue-600';
    return 'text-orange-600';
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 mb-3">
          <BarChart3 className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{t('Your Performance')}</h3>
        <p className="text-sm text-gray-600 mt-1">{t('Sentence pronunciation accuracy')}</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">{t('Average Accuracy')}</span>
          </div>
          <div className={`text-3xl font-bold ${getAccuracyColor(stats.averageAccuracy)}`}>
            {Math.round(stats.averageAccuracy)}%
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-green-700">{t('Perfect Sentences')}</span>
          </div>
          <div className="text-3xl font-bold text-green-600">
            {stats.perfectSentences}
          </div>
        </div>
      </div>

      {/* Motivational message */}
      {stats.averageAccuracy >= 90 && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-yellow-600" />
            <p className="text-sm font-medium text-yellow-800">
              {t('Outstanding pronunciation! Keep up the excellent work!')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
